import { Injectable } from '@angular/core';
import { UserSessionService } from './user-session.service';
import { SortObject } from '../model/sort-object';
import { BehaviorSubject, Observable } from 'rxjs';
import { RepeatableQuestions } from '../model/ui/repeatable-question';
import { environment } from 'src/environments/environment';
import {EnvService} from "../../env-service/env.service";
import {HttpClient} from "@angular/common/http";
@Injectable({
  providedIn: 'root'
})
export class MhsService {

  public allRepeatableQuestions: RepeatableQuestions[] = [];
  
  // Browser back button navigation flags - persist across component recreations
  public isNavigatingBack: boolean = false;
  public isHandlingPopstate: boolean = false;
  public isValidationFailed: boolean = false; // Track if validation failed during popstate
  public lastPushedUrl: string = ''; // Track last URL pushed to history to avoid duplicates
  // One-shot bypass for canDeactivate when an edit-from-review save is
  // navigating back to /review and router finalUrl is transiently unavailable.
  public allowReviewNavigationAfterSaveOnce: boolean = false;
  
  // MHS navigation stack - tracks the actual user's journey through the steps
  // This allows us to handle browser back button properly even with progress bar jumps
  public navigationStack: string[] = [];
  
  // Define the logical MHS step order
  public readonly MHS_STEPS = [
    '/employee-information',
    '/applicant-information',
    '/medical-history',
    '/physician-information',
    '/authorization',
    '/electronic-communication'
  ];

  constructor(private userSessionService: UserSessionService,
    private envService: EnvService,
    private http: HttpClient
  ) { }

  public AddAnswerToMhsMedicalHistory(key: string, answer: string){
    if(this.userSessionService.data.mhsAnswers === undefined){//answers do not exist
      this.addAnswerToEmptyCollection(key, answer);
    } else {//'answers exist
      let anotherAnswer = {};
      anotherAnswer[key] = answer;
      this.userSessionService.data.mhsAnswers = Object.assign(this.userSessionService.data.mhsAnswers, anotherAnswer);
    }

    if(this.userSessionService.data.mhsApplication.medicalHistory == undefined){
      this.userSessionService.data.mhsApplication.medicalHistory = {};
    }

    this.userSessionService.data.mhsApplication.medicalHistory = Object.assign(this.userSessionService.data.mhsApplication.medicalHistory, this.userSessionService.data.mhsAnswers);

    this.sortMedicalHistory();
  }

  public sortMedicalHistory(){

    let tempObjects: SortObject[] = [];
    for(let x in this.userSessionService.data.mhsApplication.medicalHistory){
      let object = new SortObject();
      object.key = x;
      object.value = this.userSessionService.data.mhsApplication.medicalHistory[x];
      tempObjects.push(object);
    }

    //sort tempObjects
    tempObjects.sort((a,b) => (a.key < b.key ? -1 : 1));

    let sortedMedicalHistory = {};
    for(let x in tempObjects){
      if(sortedMedicalHistory == undefined){
        sortedMedicalHistory[tempObjects[x].key] = tempObjects[x].value;
      } else {
        let anotherAnswer = {};
        anotherAnswer[tempObjects[x].key] = tempObjects[x].value;
        sortedMedicalHistory = Object.assign(sortedMedicalHistory, anotherAnswer);
      }
    }

    this.userSessionService.data.mhsApplication.medicalHistory = sortedMedicalHistory;
  }

  private addAnswerToEmptyCollection(key: string, answer: string){
    this.userSessionService.data.mhsAnswers = {};
    this.userSessionService.data.mhsAnswers[key] = answer;
  }

  private submitChildComponents = new BehaviorSubject(false);
  public submitComponent = this.submitChildComponents.asObservable();

  updateSubmission(message: boolean) {
    this.submitChildComponents.next(message)
  }

  //resubmit employee info
  private resubmitEmployeeInfoComponent = new BehaviorSubject(false);
  public submitEmployeeInfo = this.resubmitEmployeeInfoComponent.asObservable();

  resubmitEmployeeInfo(message: boolean) {
    this.resubmitEmployeeInfoComponent.next(message)
  }

  //resubmit applicant info
  private resubmitApplicantInfoComponent = new BehaviorSubject(false);
  public submitApplicantInfo = this.resubmitApplicantInfoComponent.asObservable();

  resubmitApplicantInfo(message: boolean) {
    this.resubmitApplicantInfoComponent.next(message)
  }

  //resubmit medical history info
  private resubmitMedicalHistoryComponent = new BehaviorSubject(false);
  public submitMedicalHistory = this.resubmitMedicalHistoryComponent.asObservable();

  resubmitMedicalHistory(message: boolean) {
    this.resubmitMedicalHistoryComponent.next(message)
  }

  //resubmit physician info
  private resubmitPhysicianComponent = new BehaviorSubject(false);
  public submitPhysician = this.resubmitPhysicianComponent.asObservable();

  resubmitPhysician(message: boolean) {
    this.resubmitPhysicianComponent.next(message)
  }

  //resubmit auth-sign
  private resubmitAuthSignComponent = new BehaviorSubject(false);
  public submitAuthSign = this.resubmitAuthSignComponent.asObservable();

  resubmitAuthSign(message: boolean) {
    this.resubmitAuthSignComponent.next(message)
  }

  //resubmit electronic-communication
  private resubmitElectronicCommComponent = new BehaviorSubject(false);
  public submitElectronicComm = this.resubmitElectronicCommComponent.asObservable();

  resubmitElectronicComm(message: boolean) {
    this.resubmitElectronicCommComponent.next(message)
  }

  public physicianErrorThrown: boolean = false;

  //resubmit auth-sign
  private throwPhysicianError = new BehaviorSubject(false);
  public throwPhysicianErrorMessage = this.throwPhysicianError.asObservable();

  updateThrowPhysicianError(message: boolean) {
    this.throwPhysicianError.next(message)
  }

  //resubmit electronic-communication
  private resubmitElectronicCommunicationComponent = new BehaviorSubject(false);
  public submitAuthElectronicCommunication = this.resubmitElectronicCommunicationComponent.asObservable();

  resubmitElectronicCommunication(message: boolean) {
    this.resubmitElectronicCommunicationComponent.next(message)
  }

  //resubmit auth-sign
  private deleteControls = new BehaviorSubject(false);
  public deletingControls = this.deleteControls.asObservable();

  updateDeleteControls(message: boolean) {
    this.deleteControls.next(message)
  }

  //mhsParentKey
  private parentKey = new BehaviorSubject('');
  public mhsParentKey = this.parentKey.asObservable();

  updateMhsParentKey(message: string) {
    this.parentKey.next(message);
  }

  //mhsIndexToRemove
  private indexToRemove = new BehaviorSubject(-1);
  public mhsIndexToRemove = this.indexToRemove.asObservable();

  updateMhsIndexToRemove(message: number) {
    this.indexToRemove.next(message);
  }

  //resubmit auth-sign
  public isPregnancyQuestionControl = new BehaviorSubject(false);
  public isPregnancyQuestion = this.isPregnancyQuestionControl.asObservable();

  isValidEmailAddress(email: string): Observable<any> {
      const url = environment.getApiUrl(environment.isValidEmailAddress(email),
              this.envService.mhsApiEndpoint,
              this.envService.apiSubscriptionId);
             // alert(url);
            return this.http.get<Boolean>(url);
      
    }

}